import React from 'react';
import './App.css';
import { Routes, Route, useNavigate, useLocation } from 'react-router-dom';
import AboutPage from './about/AboutPage';
import HomePage from './home/Homepage';
import Favourites from './favourites/Favourites';
import PageNotFound from './PageNotFound';
import Navigationbar from './common/Navbar';
import SignUpPage from './auth/Signup';
import LoginPage from './auth/Login';
import ForgotPassword from './auth/ForgotPassword';
import MyProfilePage from './profiles/MyProfile/MyProfile';
import ProfilePage from './profiles/Profile';
import CreateProfilePage from './profiles/CreateProfile';
import SuccessStories from './SuccessStories/SuccessStories';
import Footer from './common/Footer';
import RequireAuth from './auth/RequireAuth';
import DoesNotRequireAuth from './auth/DoesNotRequireAuth';
import useAuth from './hooks/useAuth';
import { getMyProfile, refreshAccessToken } from '../services/auth';
import { AdminHome } from './admin/AdminHome';
import RequireAdmin from './auth/RequireAdmin';
import { AdminViewProfile } from './admin/AdminViewProfile';
import RequirePayment from './auth/RequirePayment';
import ResetPasswordPage from './auth/ResetPassword';
import { AdminCreatePofile } from './admin/AdminCreatePofile';
import { AdminResetPassword } from './admin/AdminResetPassword';
import { AdminReportPage } from "./admin/AdminReportPage";
import { LOCALSTORAGE_FILTER_PREFERENCE_KEY, LOCALSTORAGE_TOKEN_KEY } from "../services/Constants";
import { filterExpander } from "./common/utilities/FilterTranslator";
import { intialFilterState } from "../services/profile";
import Loading from './loading/Loading';
import ProfileReminder from './common/ProfileReminder';
import AdminPasswordManagement from './admin/AdminPasswordManagement';
import { ALL_PROFILE_FIELDS } from './constants/Constants';
import RequireAcknowledgement from './acknowledgements/RequireAcknowledgement';
import AcknowledgementsPage from './acknowledgements/AcknowledgementsPage';
import PreviewedProfiles from './preview/PreviewedProfiles';
// import axios from 'axios';

function getDataFromLocalStorage() {
	// console.log('fetching token from local storage');
	const tokenString = localStorage.getItem(LOCALSTORAGE_TOKEN_KEY);
	const filterPreferenceString = localStorage.getItem(LOCALSTORAGE_FILTER_PREFERENCE_KEY);
	var token = {}, filterPreference = {};

	try {
		token = JSON.parse(tokenString);
		filterPreference = JSON.parse(filterPreferenceString);
	} catch (error) {
		console.error('Error parsing the cache values', error)
	}

	return {
		token,
		filterPreference
	};
}

function App() {
	const {
		profile,
		getToken,
		setAuth,
		setProfile,
		logout,
		addFavouritesToState,
		userRole,
		setUserRole,
		setIsUserPaymentValid,
		addReportsToState,
		loadFilters,
		extendProfileCompleteReminder,
		setProfileCompleteReminderTime,
		profileCompleteReminderTime,
		setUserSignedTncVersion, 
		shouldUserSignTnc,
		setUserSignedDeclarationTime,
		shouldUserProvideDeclaration,
		setUserViewedPreviewIntroTime
	} = useAuth();
	// need code to check if user has created profile and redirect accordingly
	const [loading, setLoading] = React.useState(true);
	const [shouldRenderProfileUpdateBanner, setShouldRenderProfileUpdateBanner] = React.useState(false);
	// const [loadedSuccessfully, setLoadedSuccessfully] = React.useState(true);
	const navigate = useNavigate();
	const location = useLocation();
	
	React.useEffect(() => {		
		if(!profile)
			return;
		const requiredIncomplete = ALL_PROFILE_FIELDS.some(field => !profile[field] || profile[field].length === 0);
		const isProfileReminderTimeExpired = new Date(profileCompleteReminderTime) < new Date()
		var isExpired = isProfileReminderTimeExpired && getToken()?.token && requiredIncomplete && userRole!=='admin' && location.pathname !== '/createprofile' && location.pathname !== '/myprofile'
		// if user has not signed tnc or has not given declaration dont give banner, as they cannot see my profile anyways
		setShouldRenderProfileUpdateBanner(isExpired && !shouldUserSignTnc && !shouldUserProvideDeclaration);
	}, [profileCompleteReminderTime, profile, userRole, location, getToken, shouldUserProvideDeclaration, shouldUserSignTnc])
	
	// Handle the click to go to my profile page
	const handleCompleteProfileClick = async () => {
		await extendProfileCompleteReminder();
		navigate('/myprofile');
	};
	

	React.useEffect(() => {
		if (!getToken()?.token) {
			// console.log('No Token present in context');
			let localStorageData = getDataFromLocalStorage();
			let localStorageToken = localStorageData?.token, localStorageFilter = localStorageData?.filterPreference;
			if (localStorageToken && !profile?.firstName) {
				// console.log('token found in local', localStorageToken);
				getMyProfile(localStorageToken)
					.then((getProfile) => {
						if (getProfile.errors) {
							console.error(
								'failure in getting my profile',
								getProfile.errors
							);
							setLoading(false);
							logout();
						} else {
							let profile = getProfile.data.getMe.profile;
							let roles = getProfile.data.getMe.roles;

							let isUserPaymentValid =
								parseFloat(
									getProfile.data.getMe.user
										.remainingValidityMillis
								) > 0;
							isUserPaymentValid && setIsUserPaymentValid(true);
							setProfileCompleteReminderTime(getProfile.data.getMe.user.profileReminderTime)
							setProfile(profile);
							if (roles.length > 0 && roles[0] === 'admin')
								setUserRole('admin');
							let favourites = getProfile.data.getMe.favouriteIds;
							let reports = getProfile.data.getMe.reportedUsers;
							addFavouritesToState(favourites);
							// setLoadedSuccessfully(token !== null);
							setAuth({ token: localStorageToken });
							addReportsToState(reports);
							var filterState = {};
							if(localStorageFilter && Object.keys(localStorageFilter).length > 0) {
								filterState = filterExpander({...intialFilterState()}, localStorageFilter);								
							} else {
								let filterPreferenceString = getProfile.data.getMe.user.filterPreferences;
								const parsedFilterState = filterPreferenceString ? JSON.parse(filterPreferenceString) : {}
								filterState = filterExpander({...intialFilterState()}, parsedFilterState);
							}
							setUserSignedTncVersion(getProfile.data.getMe.acknowledgements.lastTnCVersion);
							setUserSignedDeclarationTime(getProfile.data.getMe.acknowledgements.lastDeclarationTime);
							setUserViewedPreviewIntroTime(getProfile.data.getMe.acknowledgements.lastPreviewTime);
							setLoading(false);

							loadFilters(filterState)
							refreshAccessToken(localStorageToken).then(
								(refreshedAccessToken) => {
									setAuth({ token: refreshedAccessToken });
								}
							);
						}
					})
					.catch((error) => {
						setLoading(false);
						console.log(
							'error in loading profile using token',
							error
						);
					});
			} else {
				setLoading(false);
			}
		} else {
			setLoading(false);
		}
	}, []);

	return loading ? (
		<Loading/>
	) : (
		<div className='container-fluid scrollbar'>
			<Navigationbar />
			<div className='min-h-screen'>
			<Routes>
				{/* routes to protect */}
				<Route element={<RequireAuth />}>
					{/* Non Payment Required Paths */}
					<Route
						exact
						path='/createprofile'
						element={<CreateProfilePage />}
					/>
					
					<Route element={<RequireAcknowledgement />}>
						<Route exact path='/user-acknowledgements' element={<AcknowledgementsPage />} />
						<Route exact path='/' element={<HomePage />} />
						<Route exact path='/home' element={<HomePage />} />
						<Route exact path='/myprofile' element={<MyProfilePage />} />
						<Route exact path='/profile/:id' element={<ProfilePage />} />
						<Route exact path='/previewed-profiles' element={<PreviewedProfiles />} />
						<Route element={<RequirePayment />}>
							<Route
								exact
								path='/favourites'
								element={<Favourites />}
							/>
						</Route>
					</Route> 
					<Route
						exact
						path='/administratorpath'
						element={<RequireAdmin />}
					>
						<Route exact path='home' element={<AdminHome />} />
						<Route
							exact
							path='viewprofile/:id'
							element={<AdminViewProfile />}
						/>
						<Route
							exact
							path='passwordManagement'
							element={<AdminPasswordManagement/>}
						/>
						<Route
							exact
							path='createprofile'
							element={<AdminCreatePofile />}
						/>
						<Route
							exact
							path='resetpassword/:id'
							element={<AdminResetPassword />}
						/>
						<Route
							exact
							path='reports'
							element={<AdminReportPage />}
						/>
					</Route>
				</Route>

				{/* Routes not meant for logged in users */}
				<Route element={<DoesNotRequireAuth />}>
					<Route exact path='/signup' element={<SignUpPage />} />
					<Route exact path='/login' element={<LoginPage />} />
					<Route
						exact
						path='/forgot-password'
						element={<ForgotPassword />}
					/>
					<Route
						path='/reset-password'
						element={<ResetPasswordPage />}
					/>
				</Route>

				{/* Publicly available */}
				<Route
					exact
					path='/successStories'
					element={<SuccessStories />}
				/>
				<Route path='/about' element={<AboutPage />} />
				<Route path='*' element={<PageNotFound />} />
			</Routes>
			</div>
			<Footer />
			{shouldRenderProfileUpdateBanner && <ProfileReminder onClick={handleCompleteProfileClick} onDismiss={extendProfileCompleteReminder} />}
		</div>
	);
}

export default App;
